import { useMemo } from 'react';

import { Banner, sizes, useToastContext } from '@rtkwlf/fenrir-react';
import type { AlertConfigurationRule } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { useAlertConfigurationRules } from '@apis/rendall/queries/use-alert-configuration-rules';
import { useUpdateConfigurationRules } from '@apis/rendall/queries/use-update-configuration-rules';
import { AlertForm } from '@packages/components/alert-configuration-rules/forms/alert-form/alert-form';
import { hasIdenticalAlertEntry } from '@packages/components/alert-configuration-rules/has-identical-alert-entry/has-identical-alert-entry';
import { useUpdateAlertConfiguration } from '@packages/components/alert-configuration-rules/use-update-alert-configuration/use-update-alert-configuration';
import { Box } from '@packages/fenrir/components';
import { TOAST_DURATION } from 'ae-constants/toast';

import type { TicketRuleTypeSelectValue } from '../../ticket-rule-type-select/use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';
import { ActionErrorMessage } from '../../ticketAction.utils';

interface TicketAlertActionFormProps {
  ruleType: Exclude<TicketRuleTypeSelectValue, 'other'>;
  submitButtonText: string;
  ticketId: string;
  isCloseTicketPending: boolean;
  errorMessage: string | null | undefined;
  onCloseErrorMessage: () => void;
}

export function TicketAlertActionForm({
  ruleType,
  submitButtonText,
  ticketId,
  isCloseTicketPending,
  errorMessage,
  onCloseErrorMessage,
}: TicketAlertActionFormProps) {
  const { t } = useTranslation('ticket');
  const { addToast } = useToastContext();
  const { isFetching: isAlertFetching, refetch: fetchAlertConfigRule } =
    useAlertConfigurationRules({
      select: (data) => data[ruleType],
      enabled: false,
    });
  const updateAlertConfiguration = useUpdateAlertConfiguration();

  const {
    mutateAsync: updateConfiguration,
    isPending: isUpdateConfigurationRulesPending,
  } = useUpdateConfigurationRules({ ticketId });

  const isSubmitting =
    isAlertFetching ||
    isUpdateConfigurationRulesPending ||
    isCloseTicketPending;

  const buttonLabels = useMemo(
    () => ({ reset: t('cancelCommentButton'), submit: submitButtonText }),
    [submitButtonText, t],
  );

  const handleSubmit = async (newData: AlertConfigurationRule['data']) => {
    try {
      const { data: alert } = await fetchAlertConfigRule({
        throwOnError: true,
      });

      if (hasIdenticalAlertEntry(alert?.data, newData)) {
        addToast({
          appearance: 'danger',
          title: t('createAlertConfigError.title'),
          duration: TOAST_DURATION,
          message: t(
            `createAlertConfigError.description.duplicateEntry.${ruleType}`,
          ),
        });
        return;
      }

      const updatedRuleConfig = alert
        ? updateAlertConfiguration(alert, newData)
        : { version: 0, data: [newData] };

      await updateConfiguration({
        alertId: ruleType,
        configRequestBody: updatedRuleConfig,
      });
    } catch {
      addToast({
        appearance: 'danger',
        title: t('createAlertConfigError.title'),
        duration: TOAST_DURATION,
        message: t('createAlertConfigError.description.default'),
      });
    }
  };

  return (
    <Box sx={{ marginTop: sizes['0.5rem'] }}>
      <AlertForm
        onSubmit={handleSubmit}
        alertId={ruleType}
        isSubmitting={isSubmitting}
        buttonLabels={buttonLabels}
        notice={
          <>
            <Banner
              title={t('actionAlertBanner.title')}
              description={t('actionAlertBanner.description')}
            />
            {errorMessage && (
              <ActionErrorMessage
                onClose={onCloseErrorMessage}
                errorMessage={errorMessage}
              />
            )}
          </>
        }
      />
    </Box>
  );
}
